// Suggest-a-game panel — a slide-in shared by the main app (play-mode popover,
// in-game invite modal, friends screen) and the library app (game detail).
// Sends a viral game recommendation to a friend (NOT a play-together invite):
// POST /api/game-suggestions delivers a notification + DM card with a one-click
// solo launch on the recipient's side.
//
// Self-contained on purpose (mirrors report-game-panel.js): it injects its own
// DOM once and depends only on the shared DOM helper, so both bundles can
// import it without pulling in app-specific auth/api modules. Auth and
// friendship are enforced server-side.
//
// Two entry shapes:
//   openSuggestGame({ system_id, rom_filename, rom_hash?, game_id?, game_title?, onClose? })
//     → pick a friend (game already known)
//   openSuggestGame({ to_username, onClose? })
//     → pick one of my recent games (friend already known)

import { h, on } from './dom.js';
import { systemTag } from '../system-label.js';

const MAX_NOTE_LEN = 280;

let els = null;
let ctx = {};
let escHandler = null;
let generation = 0;
let sessionOpen = false;

// ── Feature flag (for surfaces without moduleRegistry, e.g. the library app).
// Launch-dark semantics: an absent flag (old server) reads as DISABLED.
let _flagsPromise = null;
export function suggestFeatureEnabled() {
  if (!_flagsPromise) {
    _flagsPromise = fetch('/api/admin/features')
      .then(r => (r.ok ? r.json() : {}))
      .catch(() => ({}));
  }
  return _flagsPromise.then(f => f.game_suggestions === true);
}

function build() {
  if (els) return els;

  const title = h('h2', { class: 'suggest-panel-title', id: 'suggest-game-title' }, 'Suggest a game');
  const closeBtn = h('button', {
    type: 'button', class: 'suggest-panel-close', 'aria-label': 'Close',
  }, '×');

  const contextBox = h('div', { class: 'suggest-context' });
  const note = h('input', {
    class: 'suggest-note-input', type: 'text', maxlength: String(MAX_NOTE_LEN),
    placeholder: 'Add a note (optional)…', 'aria-label': 'Optional note',
  });
  const search = h('input', {
    class: 'suggest-search-input', type: 'search',
    placeholder: 'Search friends…', 'aria-label': 'Search friends',
  });
  const list = h('div', { class: 'suggest-list' });
  const error = h('div', { class: 'suggest-error', role: 'status', 'aria-live': 'polite', style: { display: 'none' } });

  const body = h('div', { class: 'suggest-panel-body' },
    contextBox, note, search, list, error,
  );
  const content = h('div', { class: 'suggest-panel-content' },
    h('div', { class: 'suggest-panel-header' }, title, closeBtn),
    body,
  );
  const panel = h('aside', {
    class: 'suggest-panel hidden', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'suggest-game-title',
  }, h('div', { class: 'suggest-panel-backdrop' }), content);
  document.body.appendChild(panel);

  on(closeBtn, 'click', closeSuggestGame);
  on(panel.querySelector('.suggest-panel-backdrop'), 'click', closeSuggestGame);

  els = { panel, content, contextBox, note, search, list, error };
  return els;
}

function showError(msg) {
  els.error.textContent = msg;
  els.error.style.display = '';
}

function clearError() {
  els.error.textContent = '';
  els.error.style.display = 'none';
}

function emptyState(message) {
  els.list.replaceChildren(h('div', { class: 'suggest-empty' }, message));
}

function gameLabel(game) {
  const bits = [];
  if (game.system_id) bits.push(systemTag(game.system_id));
  return bits.join(' · ');
}

function renderContext({ game, toUsername }) {
  // Unlike h(), replaceChildren() renders null children as the string
  // "null" — filter them out.
  els.contextBox.replaceChildren(...[
    h('span', { class: 'suggest-context-label' }, 'Suggesting'),
    h('div', { class: 'suggest-context-game' },
      game ? (game.game_title || game.rom_filename) : 'Pick a recent game'),
    game && gameLabel({ system_id: game.system_id })
      ? h('div', { class: 'suggest-context-meta suggest-context-system' }, gameLabel({ system_id: game.system_id }))
      : null,
    toUsername ? h('div', { class: 'suggest-context-meta' }, `to @${toUsername}`) : null,
  ].filter(Boolean));
}

async function sendSuggestion(btn, { toUsername, game }) {
  const owner = generation;
  clearError();
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = 'Sending…';
  try {
    const res = await fetch('/api/game-suggestions', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to_username: toUsername,
        system_id: game.system_id,
        rom_filename: game.rom_filename,
        rom_hash: game.rom_hash || undefined,
        game_title: game.game_title || undefined,
        message: els.note.value.trim() || undefined,
      }),
    });
    if (owner !== generation || !sessionOpen) return;
    if (res.status === 201) {
      btn.textContent = 'Suggested ✓';
      btn.classList.add('suggest-sent');
      return;
    }
    if (res.status === 409) {
      btn.textContent = 'Already sent';
      return;
    }
    if (res.status === 401) {
      btn.disabled = false;
      btn.textContent = original;
      showError('Please sign in to suggest games.');
      return;
    }
    if (res.status === 403) {
      btn.textContent = 'Not friends';
      return;
    }
    if (res.status === 429) {
      btn.disabled = false;
      btn.textContent = original;
      showError('Daily suggestion limit reached. Try again tomorrow.');
      return;
    }
    const data = await res.json().catch(() => ({}));
    if (owner !== generation || !sessionOpen) return;
    btn.disabled = false;
    btn.textContent = original;
    showError(data.error === 'rom_not_found'
      ? 'That game is not in the library anymore.'
      : 'Something went wrong. Please try again.');
  } catch {
    if (owner !== generation || !sessionOpen) return;
    btn.disabled = false;
    btn.textContent = original;
    showError('Network error. Please check your connection.');
  }
}

// ── Friend picker (game known) ─────────────────────────────────────────

function renderFriendRows(friends, game) {
  if (!friends.length) {
    emptyState('No friends match.');
    return;
  }
  els.list.replaceChildren(...friends.map((f) => {
    const name = f.displayUsername || f.username;
    const playing = f.presence?.current_game?.title;
    const btn = h('button', { class: 'suggest-row-btn', type: 'button' }, 'Suggest');
    on(btn, 'click', () => sendSuggestion(btn, { toUsername: f.username, game }));
    return h('div', { class: 'suggest-row' },
      h('div', { class: 'suggest-row-text' },
        h('span', { class: 'suggest-row-name' }, name),
        h('span', { class: 'suggest-row-meta' },
          `@${f.username}${playing ? ` · playing ${playing}` : ''}`),
      ),
      btn,
    );
  }));
}

async function showFriendPicker(game) {
  const owner = generation;
  els.search.style.display = '';
  emptyState('Loading friends…');
  let friends;
  try {
    const res = await fetch('/api/friends', { credentials: 'include' });
    if (owner !== generation || !sessionOpen) return;
    if (res.status === 401) {
      emptyState('Sign in to suggest games to your friends.');
      return;
    }
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    if (owner !== generation || !sessionOpen) return;
    friends = Array.isArray(data) ? data : (data.friends || []);
  } catch {
    if (owner !== generation || !sessionOpen) return;
    emptyState('Could not load your friends list.');
    return;
  }
  if (!friends.length) {
    emptyState('No friends yet. Add friends to share suggestions.');
    return;
  }
  renderFriendRows(friends, game);
  els.search.oninput = () => {
    const q = els.search.value.trim().toLowerCase();
    renderFriendRows(
      q
        ? friends.filter(f =>
            f.username?.toLowerCase().includes(q)
            || f.displayUsername?.toLowerCase().includes(q))
        : friends,
      game,
    );
  };
}

// ── Recent-games picker (friend known) ─────────────────────────────────

async function showGamePicker(toUsername) {
  const owner = generation;
  els.search.style.display = 'none';
  emptyState('Loading your recent games…');
  let games;
  try {
    const res = await fetch('/api/me/recent-games?limit=15', { credentials: 'include' });
    if (owner !== generation || !sessionOpen) return;
    if (res.status === 401) {
      emptyState('Sign in to suggest games.');
      return;
    }
    if (!res.ok) throw new Error(String(res.status));
    const data = await res.json();
    if (owner !== generation || !sessionOpen) return;
    // A suggestion needs a launchable file — skip rows the server could not
    // resolve to a ROM filename.
    games = (data.games || []).filter(g => g.filename && g.system_id);
  } catch {
    if (owner !== generation || !sessionOpen) return;
    emptyState('Could not load your recent games.');
    return;
  }
  if (!games.length) {
    emptyState('No recently played games yet. Play something first!');
    return;
  }
  els.list.replaceChildren(...games.map((g) => {
    const row = h('button', { class: 'suggest-row suggest-row-game', type: 'button' },
      h('div', { class: 'suggest-row-text' },
        h('span', { class: 'suggest-row-name' }, g.title),
        h('span', { class: 'suggest-row-meta' }, systemTag(g.system_id)),
      ),
      h('span', { class: 'suggest-row-pick' }, '›'),
    );
    on(row, 'click', () => {
      const game = {
        system_id: g.system_id,
        rom_filename: g.filename,
        game_title: g.title,
        game_id: g.game_id,
      };
      renderContext({ game, toUsername });
      const btn = h('button', { class: 'suggest-row-btn suggest-confirm-btn', type: 'button' },
        `Suggest to @${toUsername}`);
      on(btn, 'click', () => sendSuggestion(btn, { toUsername, game }));
      els.list.replaceChildren(
        h('div', { class: 'suggest-confirm' },
          btn,
          h('button', {
            class: 'suggest-back-btn', type: 'button',
            onclick: () => { renderContext({ game: null, toUsername }); showGamePicker(toUsername); },
          }, '‹ Pick a different game'),
        ),
      );
    });
    return row;
  }));
}

// ── Open / close ───────────────────────────────────────────────────────

export function openSuggestGame(context = {}) {
  build();
  if (sessionOpen) closeSuggestGame();
  const owner = ++generation;
  sessionOpen = true;
  ctx = context;
  clearError();
  els.note.value = '';
  els.search.value = '';
  els.search.oninput = null;

  const hasGame = !!(context.system_id && context.rom_filename);
  const game = hasGame
    ? {
        system_id: context.system_id,
        rom_filename: context.rom_filename,
        rom_hash: context.rom_hash || null,
        game_id: context.game_id ?? null,
        game_title: context.game_title || null,
      }
    : null;

  renderContext({ game, toUsername: hasGame ? null : context.to_username });
  if (hasGame) {
    showFriendPicker(game);
  } else if (context.to_username) {
    showGamePicker(context.to_username);
  } else {
    emptyState('Nothing to suggest.');
  }

  els.panel.classList.remove('hidden');
  els.panel.removeAttribute('aria-hidden');
  els.panel.removeAttribute('inert');
  els.note.focus();
  requestAnimationFrame(() => requestAnimationFrame(() => {
    if (owner === generation && sessionOpen) els.panel.classList.add('open');
  }));

  escHandler = (e) => { if (e.key === 'Escape') closeSuggestGame(); };
  document.addEventListener('keydown', escHandler);
}

export function closeSuggestGame() {
  if (!els || !sessionOpen) return;
  const owner = ++generation;
  sessionOpen = false;
  els.panel.classList.remove('open');
  els.panel.setAttribute('aria-hidden', 'true');
  els.panel.setAttribute('inert', '');
  const onEnd = () => {
    els.content.removeEventListener('transitionend', onEnd);
    if (owner === generation && !sessionOpen) els.panel.classList.add('hidden');
  };
  els.content.addEventListener('transitionend', onEnd);
  // Fallback in case transitionend doesn't fire (e.g. reduced motion).
  setTimeout(() => { if (owner === generation && !sessionOpen) els.panel.classList.add('hidden'); }, 400);
  if (escHandler) { document.removeEventListener('keydown', escHandler); escHandler = null; }
  if (typeof ctx.onClose === 'function') {
    const cb = ctx.onClose;
    ctx.onClose = null;
    cb();
  }
}
